<template>
  <div class="w-full h-full space-y-4">
    <!-- Stepper -->
    <Card class="p-0 overflow-hidden">
      <nav class="flex flex-col sm:flex-row">
        <button
          v-for="(step, index) in steps"
          :key="step.key"
          type="button"
          :disabled="!isStepEnabled(index)"
          @click="goTo(index)"
          class="group flex flex-1 items-center gap-3 px-4 py-3 text-left transition-colors border-b sm:border-b-0 sm:border-r last:border-r-0 border-border"
          :class="[
            isActive(step)
              ? 'bg-muted/60'
              : isStepEnabled(index)
                ? 'bg-card hover:bg-muted/40 cursor-pointer'
                : 'bg-card opacity-50 cursor-not-allowed',
          ]"
        >
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold"
            :class="
              isComplete(index)
                ? 'bg-primary border-primary text-primary-foreground'
                : isActive(step)
                  ? 'border-primary text-primary'
                  : 'border-border text-muted-foreground'
            "
          >
            <Check v-if="isComplete(index)" class="w-4 h-4" />
            <span v-else>{{ index + 1 }}</span>
          </span>
          <span class="min-w-0">
            <span
              class="block text-sm font-medium truncate"
              :class="isActive(step) ? 'text-foreground' : 'text-muted-foreground'"
            >
              {{ step.title }}
            </span>
            <span class="block text-xs text-muted-foreground truncate">{{ step.subtitle }}</span>
          </span>
        </button>
      </nav>
    </Card>

    <!-- Offer summary -->
    <Card v-if="wizard.retailer.retailerName" class="p-3">
      <div class="flex items-center gap-3">
        <span
          v-if="offerImage"
          class="flex items-center justify-center w-10 h-10 border border-border bg-white rounded-md shrink-0 overflow-hidden"
        >
          <img class="object-contain w-full h-full" :src="offerImage" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium leading-tight truncate">
            {{ info.offerName || wizard.retailer.retailerName }}
          </p>
          <p class="text-xs text-muted-foreground truncate">
            {{ wizard.retailer.retailerName }}<template v-if="info.country"> · {{ info.country }}</template>
          </p>
        </div>
        <div class="flex items-center gap-1.5 shrink-0">
          <Badge v-if="info.offerType" variant="secondary">{{ formatLabel(info.offerType) }}</Badge>
          <Badge v-if="cashbackSummary">{{ cashbackSummary }}</Badge>
        </div>
      </div>
    </Card>

    <router-view />
  </div>
</template>

<script>
import { Card, Badge } from '@boogi/ui'
import { Check } from 'lucide-vue-next'
import { addOfferWizard } from '@/stores/addOfferWizard'
import { breadcrumbState } from '@/stores/breadcrumb'

export const ADD_OFFER_STEPS = [
  {
    key: 'retailer',
    path: '/add-offer/retailer',
    title: 'Retailer',
    subtitle: 'Select or create a retailer',
  },
  {
    key: 'transaction',
    path: '/add-offer/transaction',
    title: 'Transaction matching',
    subtitle: 'Confirm enrichment compatibility',
  },
  {
    key: 'mids',
    path: '/add-offer/mids',
    title: 'MIDs',
    subtitle: 'Define the relevant MIDs',
  },
  {
    key: 'info',
    path: '/add-offer/info',
    title: 'Offer details',
    subtitle: 'Supplier, display and logo',
  },
  {
    key: 'rules',
    path: '/add-offer/rules',
    title: 'Cashback rules',
    subtitle: 'Configure and submit',
  },
]

export default {
  name: 'add-offer',
  components: { Card, Badge, Check },
  data() {
    return {
      wizard: addOfferWizard,
      info: addOfferWizard.offerInformation,
      steps: ADD_OFFER_STEPS,
    }
  },
  computed: {
    offerImage() {
      return this.info.offerLogo || this.wizard.retailer.retailerLogo
    },
    cashbackSummary() {
      const cond = this.info.cashbackRules?.[0]?.conditions?.[0]
      const value = cond?.value
      if (!value || value.endUserValue == null || value.endUserValue === '') return ''
      if (value.type === 'FIXED') return `${value.endUserValue} back`
      return `${value.endUserValue}% back`
    },
    currentIndex() {
      const idx = this.steps.findIndex((s) => this.$route.path === s.path)
      return idx === -1 ? 0 : idx
    },
  },
  created() {
    breadcrumbState.set([
      { label: 'Custom offers', to: '/cashback-program/custom-offers' },
      { label: this.wizard.editingOfferId ? 'Edit offer' : 'Add offer' },
    ])
  },
  unmounted() {
    breadcrumbState.clear()
  },
  methods: {
    formatLabel(value) {
      if (!value) return ''
      return value
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    isActive(step) {
      return this.$route.path === step.path
    },
    isComplete(index) {
      const flags = [
        this.wizard.retailerComplete,
        this.wizard.transactionComplete,
        this.wizard.midsComplete,
        this.wizard.offerInfoComplete,
        false,
      ]
      return index < this.currentIndex && flags[index]
    },
    isStepEnabled(index) {
      // Editing starts from a saved offer, so every step already has its data.
      if (this.wizard.editingOfferId) return true
      if (index === 0) return true
      if (index === 1) return this.wizard.retailerComplete && !this.wizard.selectedRetailer
      if (index === 2) return this.wizard.retailerComplete && this.wizard.transactionComplete
      if (index === 3) return this.wizard.midsComplete
      if (index === 4) return this.wizard.midsComplete && this.wizard.offerInfoComplete
      return false
    },
    goTo(index) {
      if (!this.isStepEnabled(index)) return
      this.$router.push(this.steps[index].path)
    },
  },
}
</script>
